import React from 'react';

export interface OptimizedImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  className?: string;
  priority?: boolean;
  sizes?: string;
  responsiveWidths?: number[];
  pictureClassName?: string;
}

/**
 * Universal Optimized Image Component
 * - Automatically serves modern WebP images with responsive srcset
 * - Supports Unsplash on-the-fly WebP formatting
 * - Fallbacks gracefully to original image
 * - Compliant with Google Lighthouse image delivery audits
 */
export const OptimizedImage: React.FC<OptimizedImageProps> = ({
  src,
  alt,
  width,
  height,
  className = '',
  priority = false,
  sizes,
  responsiveWidths,
  pictureClassName,
  loading,
  decoding = 'async',
  ...rest
}) => {
  if (!src || src.trim() === '') {
    return null;
  }

  const cleanSrc = src.trim();
  const isUpload = cleanSrc.startsWith('/uploads/');
  const isUnsplash = cleanSrc.includes('images.unsplash.com');

  // Handle /uploads/ images
  if (isUpload) {
    const cleanPath = cleanSrc.replace(/\.(png|jpe?g|webp)(\?.*)?$/i, '');
    // Strip width suffixes if present (e.g. -400w, -640w, -800w)
    const baseCleanPath = cleanPath.replace(/-(?:400|640|800|1000)w$/, '');
    const webpSrc = `${baseCleanPath}.webp`;

    // Construct responsive srcset if appropriate
    let webpSrcSet = '';

    if (responsiveWidths && responsiveWidths.length > 0) {
      webpSrcSet = responsiveWidths
        .map((w) => `${baseCleanPath}-${w}w.webp ${w}w`)
        .concat(`${webpSrc} 1000w`)
        .join(', ');
    } else if (width && width <= 450) {
      webpSrcSet = `${baseCleanPath}-400w.webp 400w, ${baseCleanPath}-640w.webp 640w, ${webpSrc} 800w`;
    } else if (width && width <= 700) {
      webpSrcSet = `${baseCleanPath}-400w.webp 400w, ${baseCleanPath}-640w.webp 640w, ${webpSrc} 1000w`;
    } else if (width && width <= 900) {
      webpSrcSet = `${baseCleanPath}-640w.webp 640w, ${baseCleanPath}-800w.webp 800w, ${webpSrc} 1200w`;
    } else {
      webpSrcSet = `${baseCleanPath}-640w.webp 640w, ${baseCleanPath}-800w.webp 800w, ${webpSrc} 1600w`;
    }

    const defaultSizes = sizes || (width ? `(max-width: 768px) 100vw, ${width}px` : undefined);

    // If cleanSrc is already a webp, render direct img with responsive srcSet
    if (cleanSrc.endsWith('.webp')) {
      return (
        <img
          src={cleanSrc}
          srcSet={webpSrcSet || undefined}
          alt={alt}
          width={width}
          height={height}
          loading={priority ? 'eager' : (loading || 'lazy')}
          decoding={decoding}
          fetchPriority={priority ? 'high' : undefined}
          className={className}
          sizes={defaultSizes}
          {...rest}
        />
      );
    }

    return (
      <picture className={pictureClassName}>
        <source
          type="image/webp"
          srcSet={webpSrcSet || webpSrc}
          sizes={defaultSizes}
        />
        <img
          src={cleanSrc}
          alt={alt}
          width={width}
          height={height}
          loading={priority ? 'eager' : (loading || 'lazy')}
          decoding={decoding}
          fetchPriority={priority ? 'high' : undefined}
          className={className}
          sizes={defaultSizes}
          onError={(e) => {
            // Graceful fallback to original image if webp is missing
            const target = e.currentTarget;
            if (target.src !== cleanSrc) {
              target.src = cleanSrc;
            }
          }}
          {...rest}
        />
      </picture>
    );
  }

  // Handle Unsplash images with auto=format&fm=webp
  if (isUnsplash) {
    const baseUrl = cleanSrc.split('?')[0];
    const w = width || 800;
    const webpUrl = `${baseUrl}?auto=format&fm=webp&fit=crop&w=${w}&q=80`;
    const webp2x = `${baseUrl}?auto=format&fm=webp&fit=crop&w=${w * 2}&q=80`;
    const defaultSizes = sizes || `(max-width: 768px) 100vw, ${w}px`;

    return (
      <picture className={pictureClassName}>
        <source
          type="image/webp"
          srcSet={`${webpUrl} 1x, ${webp2x} 2x`}
          sizes={defaultSizes}
        />
        <img
          src={cleanSrc.includes('fm=') ? cleanSrc : webpUrl}
          alt={alt}
          width={width}
          height={height}
          loading={priority ? 'eager' : (loading || 'lazy')}
          decoding={decoding}
          fetchPriority={priority ? 'high' : undefined}
          className={className}
          {...rest}
        />
      </picture>
    );
  }

  // Standard fallback
  return (
    <img
      src={cleanSrc}
      alt={alt}
      width={width}
      height={height}
      loading={priority ? 'eager' : (loading || 'lazy')}
      decoding={decoding}
      fetchPriority={priority ? 'high' : undefined}
      className={className}
      {...rest}
    />
  );
};
